import React, { useReducer } from 'react'

import { AccordionTitle } from '@/components/Shared/AccordionComponent'
import { DEFAULT_DATE_FORMAT } from '@/constants/scenarioBuilder.constant'
import { AccordionKeys } from '@/enums/portfolio-optimization.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IPortfolio } from '@/interfaces/portfolioOptimization.interface'
import {
  setAccordionComplete,
  setSelectedPortfolios,
} from '@/states/portfolioOptmization.slice'
import { SEMANTIC_COLOR_PALETTE } from '@/theme/theme-config'
import { Box, Button, Typography } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionActions from '@mui/material/AccordionActions'
import AccordionDetails from '@mui/material/AccordionDetails'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'
import { PortfoliosGrid } from '../../Portfolios/PortfoliosGrid'
import StatusIndicator from '../../StatusIndicator'
import {
  selectPortfolioReducer,
  selectPortfoliosInitialState,
} from './SelectPortfoliosAccordionState'

const SelectPortfolioAccordion: React.FC<{
  selectedPortfolios: IPortfolio[]
  updateSelectedPortfolios: (portfolios: IPortfolio[]) => void
}> = ({ selectedPortfolios, updateSelectedPortfolios }) => {
  const appDispatch = useDispatch()
  const isAccordionComplete = useAppSelector(
    (s) => s.portfolioOptimization.accordions[AccordionKeys.SELECT_PORTFOLIOS],
  )
  const [state, dispatch] = useReducer(
    selectPortfolioReducer,
    selectPortfoliosInitialState,
  )

  const showSelectedPortfolios = (portfolios: IPortfolio[]): string => {
    // when no portfolio is selected
    if (!portfolios.length) return 'No Portfolio(s) Selected'
    let showNoOfPortfolios = ''

    if (portfolios.length > 1) {
      showNoOfPortfolios = `, +${portfolios.length - 1}`
    }

    return `${portfolios[0].name} ${dayjs(portfolios[0].date).format(
      DEFAULT_DATE_FORMAT,
    )} ${showNoOfPortfolios}`
  }

  // Whenever the local state changes, update the global context

  return (
    <Accordion
      expanded={state.isExpanded}
      onChange={() => dispatch({ type: 'TOGGLE_EXPANDED' })}
    >
      <AccordionTitle aria-controls='panel1-content' id='panel1-header'>
        <Box display={'flex'} gap={1}>
          <Typography variant='h3'>Select Portfolio:</Typography>
          <Typography variant='h4'>
            {showSelectedPortfolios(state.selectedPortfolios)}
          </Typography>
        </Box>
        <StatusIndicator isComplete={isAccordionComplete} />
      </AccordionTitle>
      <AccordionDetails sx={{ paddingY: 1 }}>
        <PortfoliosGrid
          onSelectPortfolios={(portfolios: IPortfolio[]) => {
            updateSelectedPortfolios(portfolios)
          }}
        />
      </AccordionDetails>
      <AccordionActions
        sx={{
          padding: 2,
          borderTop: '1px solid',
          borderTopColor: SEMANTIC_COLOR_PALETTE.border.light,
        }}
      >
        <Button
          size='small'
          variant='contained'
          disabled={!selectedPortfolios.length}
          onClick={() => {
            dispatch({
              type: 'SET_PORTFOLIOS',
              payload: selectedPortfolios,
            })
            dispatch({ type: 'TOGGLE_EXPANDED' })
            appDispatch(
              setAccordionComplete({
                accordionType: AccordionKeys.SELECT_PORTFOLIOS,
                isComplete: true,
              }),
            )
            appDispatch(setSelectedPortfolios(selectedPortfolios))
          }}
        >
          Use Selected Portfolio
        </Button>
      </AccordionActions>
    </Accordion>
  )
}

export default SelectPortfolioAccordion
